import React, { useEffect, useState, useRef } from "react";
import { FaUser, FaEnvelope, FaPhone, FaHome, FaRupeeSign, FaTimes, FaInfoCircle } from "react-icons/fa";
import styles from "../../styles/applicant/PaymentModal.module.css";

// Simple global state manager for concurrent payment prevention
const PaymentStateManager = {
  currentTransactionId: null,
  isProcessing: false,
  paymentWindow: null,
  listeners: new Set(),
  
  setProcessingState(transactionId, processing, window = null) {
    // If another payment is already processing, block this one
    if (this.isProcessing && this.currentTransactionId !== transactionId) {
      throw new Error('Another payment is already in progress');
    }
    
    this.currentTransactionId = transactionId;
    this.isProcessing = processing;
    this.paymentWindow = window;
    
    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener({
          transactionId,
          isProcessing: processing,
          paymentWindow: window
        });
      } catch (error) {
        console.error('PaymentStateManager listener error:', error);
      }
    });
  },
  
  cleanup(transactionId) {
    // Only cleanup if it's the current transaction
    if (this.currentTransactionId === transactionId) {
      // Close window if it exists
      if (this.paymentWindow && !this.paymentWindow.closed) {
        try {
          this.paymentWindow.close();
        } catch (error) {
          console.error('Error closing payment window:', error);
        }
      }
      
      this.currentTransactionId = null;
      this.isProcessing = false;
      this.paymentWindow = null;
      
      // Notify cleanup
      this.listeners.forEach(listener => {
        try {
          listener({ cleanup: true, transactionId });
        } catch (error) {
          console.error('PaymentStateManager cleanup listener error:', error);
        }
      });
    }
  },
  
  addListener(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  },
  
  canStartPayment(transactionId) {
    // Allow if no payment is processing OR it's the same transaction
    return !this.isProcessing || this.currentTransactionId === transactionId;
  }
};

const PaymentModal = ({ show, onClose, formData, setShowSuccessPopup }) => {
  const [tid, setTid] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentWindow, setPaymentWindow] = useState(null);
  const pollingIntervalRef = useRef(null);
  const currentTransactionId = useRef(formData.request_unique_id);
  const [concurrentBlocked, setConcurrentBlocked] = useState(false);

  useEffect(() => {
    setTid(new Date().getTime());
    currentTransactionId.current = formData.request_unique_id;
    
    return () => {
      // Clean up only for this transaction
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      // Clean up from global state
      PaymentStateManager.cleanup(currentTransactionId.current);
    };
  }, [formData.request_unique_id]);

  const oldgetAmount = () => {
    if (formData.formType === "Zone" || formData.formType === "PartPlan") {
      return "1.00";
    }
    return "250.00";
  };

  const getAmount = () => {
  if (formData.formType === "Zone") {
    //return "1.00";
    return "200.00";
  } else if (formData.formType === "PartPlan") {
    //return "1.00";
    return "700.00";
  } else {
    return "250.00";
  }
};

  const amount = getAmount();

  // Function to check payment status via API
  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/check_payment_status.php`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transaction_id: currentTransactionId.current
          })
        }
      );
      
      const result = await response.json();
      console.log("Payment status check result:", result);
      
      if (result.payment_status === "Success") {
        // Payment successful
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        setIsProcessing(false);
        
        const paymentDetails = {
          method: "Online",
          amount: result.amount || amount,
          transactionId: currentTransactionId.current,
          tracking_id: result.tracking_id || "",
          bank_ref_no: result.bank_ref_no || "",
          payment_mode: result.payment_mode || "",
          status: "Success",
          receiptNumber: `REC${Date.now().toString().slice(-8)}`
        };
        
        console.log("Setting payment details:", paymentDetails);
        
        // Clean up global state
        PaymentStateManager.cleanup(currentTransactionId.current);
        
        setShowSuccessPopup(paymentDetails);
        return true;
      } else if (result.payment_status === "Failure" || result.payment_status === "Aborted") {
        // Payment failed
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        setIsProcessing(false);
        
        // Clean up global state on failure
        PaymentStateManager.cleanup(currentTransactionId.current);
        
        alert("Payment failed. Please try again.");
        return true;
      }
      
      return false; // Still processing
    } catch (error) {
      console.error("Error checking payment status:", error);
      return false;
    }
  };

  // Start polling for payment status
  const startPaymentPolling = () => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Start polling every 3 seconds
    pollingIntervalRef.current = setInterval(async () => {
      const statusFound = await checkPaymentStatus();
      
      if (statusFound) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }, 3000); // Check every 3 seconds
    
    // Stop polling after 10 minutes
    setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsProcessing(false);
        PaymentStateManager.cleanup(currentTransactionId.current);
        console.log("Payment polling timeout after 10 minutes");
      }
    }, 600000); // 10 minutes timeout
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if another payment is already in progress
    if (!PaymentStateManager.canStartPayment(currentTransactionId.current)) {
      setConcurrentBlocked(true);
      return;
    }
    
    setIsProcessing(true);
    
    // Register with global state manager
    try {
      PaymentStateManager.setProcessingState(currentTransactionId.current, true);
    } catch (error) {
      alert(error.message);
      setIsProcessing(false);
      return;
    }
    
    // Start polling for payment status BEFORE opening window
    startPaymentPolling();
    
    const form = e.target;
    
    // Open payment window with a specific name for better control
    const paymentWin = window.open(
      "", 
      `PaymentWindow_${currentTransactionId.current}`, 
      "width=800,height=700,scrollbars=yes,resizable=yes,location=yes,toolbar=no"
    );
    
    if (!paymentWin) {
      alert("Please allow popups for this site to proceed with payment.");
      setIsProcessing(false);
      PaymentStateManager.cleanup(currentTransactionId.current);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }
    
    setPaymentWindow(paymentWin);
    
    // Update global state with payment window
    PaymentStateManager.setProcessingState(currentTransactionId.current, true, paymentWin);
    
    // Monitor the payment window
    const checkWindowClosed = setInterval(() => {
      if (paymentWin.closed) {
        clearInterval(checkWindowClosed);
        console.log("Payment window closed");
        
        // Give some time for server to process before checking status
        setTimeout(() => {
          checkPaymentStatus();
        }, 2000);
      }
    }, 1000);
    
    // Cleanup after 5 minutes
    setTimeout(() => {
      clearInterval(checkWindowClosed);
    }, 300000);
    
    // Submit form to CCAvenue - THIS OPENS THE PAYMENT GATEWAY
    form.target = `PaymentWindow_${currentTransactionId.current}`;
    form.submit();
  };

  // Early return
  if (!show) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <h2>💳 Secure Payment Checkout</h2>
          <button onClick={onClose} className={styles.closeBtn}><FaTimes /></button>
        </div>

        <div className={styles.stepIndicator}>
          <span className={styles.stepActive}>1. Confirm Details</span>
          <span className={styles.stepDivider}>›</span>
          <span>2. Payment Gateway</span>
        </div>

        {/* Warning message if concurrent payment is blocked */}
        {concurrentBlocked && (
          <div className={styles.paymentBlockedMessage}>
            <div className={styles.paymentBlockedIcon}>⏳</div>
            <h3>Another Payment in Progress</h3>
            <p>Please wait for the current payment to complete before starting a new one.</p>
            <button 
              className={styles.retryButton}
              onClick={() => setConcurrentBlocked(false)}
            >
              Check Again
            </button>
          </div>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          method="post"
          name="customerData"
          action={`${process.env.REACT_APP_SERVER_URL}/ccavRequestHandler.php`}
          className={styles.form}
          style={concurrentBlocked ? { opacity: 0.5, pointerEvents: 'none' } : {}}
        >
          {/* Hidden Fields - KEEP ALL ORIGINAL FIELDS */}
          <input type="hidden" name="tid" value={formData.request_unique_id} />
          <input type="hidden" name="merchant_id" value="2689704" />
          <input type="hidden" name="order_id" value={`ORD${Date.now()}`} />
          <input type="hidden" name="amount" value={amount} />
          <input type="hidden" name="currency" value="INR" />
          <input type="hidden" name="redirect_url" value={`https://rpsangali.vishvaconnect.site/api/ccavResponseHandler.php`} />
          <input type="hidden" name="cancel_url" value={`https://rpsangali.vishvaconnect.site/api/ccavResponseHandler.php`} />
          <input type="hidden" name="language" value="EN" />
          <input type="hidden" name="billing_name" value={formData.applicantName || ""} />
          <input type="hidden" name="billing_address" value={formData.applicantAddress || ""} />
          <input type="hidden" name="billing_city" value={formData.village || ""} />
          <input type="hidden" name="billing_state" value="Maharashtra" />
          <input type="hidden" name="billing_country" value="India" />
          <input type="hidden" name="billing_tel" value={formData.applicantMobile || ""} />
          <input type="hidden" name="billing_email" value={formData.applicantEmail || ""} />
          <input type="hidden" name="merchant_param1" value={formData.request_unique_id || ""} />
          <input type="hidden" name="merchant_param2" value={localStorage.getItem('token') || ""} />
          <input type="hidden" name="return_url" value={`${window.location.origin}/payment-success`} />

          {/* Applicant Summary */}
          <div className={styles.summaryCard}>
            <h3>🧾 Applicant Details</h3>
            <div className={styles.summaryGrid}>
              <p><FaUser /> <strong>Name:</strong> {formData.applicantName}</p>
              <p><FaEnvelope /> <strong>Email:</strong> {formData.applicantEmail}</p>
              <p><FaPhone /> <strong>Mobile:</strong> {formData.applicantMobile}</p>
              <p><FaHome /> <strong>Address:</strong> {formData.applicantAddress}</p>
              <p><FaInfoCircle /> <strong>Village:</strong> {formData.village}</p>
              <p><FaRupeeSign /> <strong>Amount Payable:</strong> ₹{amount}</p>
              <p><strong>Transaction ID:</strong> {formData.request_unique_id}</p>
            </div>
          </div>
    <h8>If the pop-up is not opening, it may be blocked by your browser’s pop-up blocker.</h8>

          <h8>Please enable pop-ups in your browser and try again.</h8>
          <br/>
          <h1></h1>
          {/* Processing indicator */}
          {isProcessing && (
            <div className={styles.processingIndicator}>
              <div className={styles.spinner}></div>
              <p>Processing payment... Please complete payment in the popup window.</p>
              <p className={styles.smallText}>Do not close this window until payment is complete.</p>
              <p className={styles.smallText}>Transaction ID: {formData.request_unique_id}</p>
            </div>
          )}

          {/* Confirmation */}
          <div className={styles.confirmationBox}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" required disabled={isProcessing || concurrentBlocked} /> 
              I confirm that the above details are correct and I wish to proceed to payment.
            </label>
          </div>

          {/* Footer */}
          <div className={styles.footer}>
            <button 
              type="button" 
              className={styles.cancelBtn} 
              onClick={() => {
                PaymentStateManager.cleanup(currentTransactionId.current);
                onClose();
              }}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className={styles.payBtn}
              disabled={isProcessing || concurrentBlocked}
            >
              {isProcessing ? "Processing..." : "Proceed to Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;